import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';
import './BlogPage.css';

const BLOG_CANONICAL_URL = 'https://circumvent.io/blog';
const BLOG_PAGE_TITLE = 'Cloud security validation insights | Circumvent';
const BLOG_PAGE_DESCRIPTION =
  'Notes on finding validation, severity vs real risk, and routing verified work to engineering. From the Circumvent team.';
const STARTUP_DAILY_ARTICLE_URL =
  'https://www.startupdaily.net/topic/funding/a-cybersecurity-founder-who-had-a-100-million-exit-is-back-with-a-new-idea-and-a-9-2-million-seed-round-from-paladin-capital/';

const BLOG_POSTS = [
  {
    id: 'security-at-machine-speed',
    title: 'Security at machine speed: Why finding vulnerabilities is no longer the hard part',
    excerpt:
      'Security teams still lose time between finding a vulnerability and doing something meaningful about it. Discovery is accelerating. Response is not.',
    href: '/blog/security-at-machine-speed',
    isExternal: false,
    publishedAt: '2026-10-26',
    category: 'Perspective',
    imageSrc: '/images/blog/security-at-machine-speed.jpg',
    imageAlt:
      'A speedometer and a server spilling security findings, across a gap from a magnifying glass over one document and a short chain of cloud, code, and ticket icons.',
    imageFit: 'cover',
  },
  {
    id: 'soc2-type-2-attestation',
    title: 'Circumvent Achieves SOC 2® Type 2 Attestation',
    excerpt:
      'Circumvent has completed its SOC 2 Type 2 examination with a clean attestation report, confirming operating effectiveness of our security controls.',
    href: '/blog/soc-2-type-2-attestation',
    isExternal: false,
    publishedAt: '2026-10-12',
    category: 'Compliance',
    imageSrc: '/images/blog/soc2-aicpa-seal.png',
    imageAlt: 'SOC 2 seal for service organizations.',
    imageFit: 'contain',
    imageSurface: 'dark',
  },
  {
    id: 'multi-tenant-oidc-for-aws-access',
    title: 'Multi-tenant OpenID Connect (OIDC) for SaaS vendors on AWS',
    excerpt:
      'Why OIDC beats an ExternalID for SaaS vendors that serve many customers, and why each customer should get their own issuer URL.',
    href: '/blog/multi-tenant-oidc-for-aws-access',
    isExternal: false,
    publishedAt: '2026-10-09',
    category: 'Engineering',
    imageSrc: '/images/blog/oidc-per-customer-issuer.svg',
    imageAlt:
      'Three issuer URLs, one for each customer tenant, each trusted by that customer IAM role.',
    imageFit: 'contain',
    imageSurface: 'dark',
  },
  {
    id: 'containing-the-agentic-blast-radius',
    title: 'Containing the agentic blast radius',
    excerpt:
      'Bedrock AgentCore gives you isolation and credential custody, but not per-run egress. Here is how we scope an agent’s network and credentials per invocation.',
    href: '/blog/containing-the-agentic-blast-radius',
    isExternal: false,
    publishedAt: '2026-09-14',
    category: 'Engineering',
    imageSrc: '/images/blog/arch-token-vending.svg',
    imageAlt:
      'Architecture diagram: control plane and token vending machine issuing a scoped token across a trust boundary into an AgentCore agent sandbox.',
    imageFit: 'contain',
  },
  {
    id: 'severity-rankings-dont-shrink-risk',
    title: "Severity rankings don't shrink risk",
    excerpt:
      'Sorting by reported severity feels like prioritization. Too often it burns engineering time on theoretical risk while verified exposures wait.',
    href: '/blog/severity-rankings-dont-shrink-risk',
    isExternal: false,
    publishedAt: '2026-08-24',
    category: 'Insights',
    imageSrc: '/images/blog/severity-rankings-dont-shrink-risk.png',
    imageAlt:
      'Illustration contrasting a shiny vanity metrics dashboard on the left with a severed attack-path chain representing real risk reduction on the right.',
    imageFit: 'cover',
  },
  {
    id: 'if-its-not-shipped-its-not-fixed',
    title: "If it's not shipped, it's not fixed",
    excerpt:
      'Automated remediation often moves the queue onto engineering. Risk drops when a change ships and is verified; not when a bot opens a PR.',
    href: '/blog/if-its-not-shipped-its-not-fixed',
    isExternal: false,
    publishedAt: '2026-08-12',
    category: 'Insights',
    imageSrc: '/images/blog/if-its-not-shipped-its-not-fixed.png',
    imageAlt:
      'Illustration of unfinished work piled on one side of a chasm, with a broken bridge blocking the way across.',
    imageFit: 'cover',
  },
  {
    id: 'stop-trying-to-fit-new-technology-into-old-categories',
    title: 'Stop Trying to Fit New Technology Into Old Categories',
    excerpt: 'Why every breakthrough product gets misunderstood before it gets adopted.',
    href: '/blog/stop-trying-to-fit-new-technology-into-old-categories',
    isExternal: false,
    publishedAt: '2026-08-04',
    category: 'Perspective',
    imageSrc: '/images/blog/stop-fitting-new-tech-into-old-categories.png',
    imageAlt:
      "A confused businessman stands between signposts for CNAPP, SIEM, ASPM, Exposure Management, and Vulnerability Management, holding a Circumvent sign, next to handwritten text reading 'Where do you fit?'",
    imageFit: 'cover',
  },
  {
    id: 'the-feature-trap-security-industry-outcomes',
    title:
      "The Feature Trap that's ensnared the Security industry and stopped it focusing on Outcomes",
    excerpt:
      'Security tools competed on control counts and feature parity. That output race created noise. Why Circumvent is rebuilding around outcomes instead.',
    href: '/blog/the-feature-trap-security-industry-outcomes',
    isExternal: false,
    publishedAt: '2026-07-13',
    category: 'Perspective',
    imageSrc: '/images/blog/feature-trap-battlecard-vs-outcomes-v4.jpg',
    imageAlt:
      'Comparison of feature-parity battle cards against an outcomes-focused approach to security posture.',
    imageFit: 'cover',
  },
  {
    id: 'i-helped-create-the-problem-im-now-solving',
    title: "I helped create the problem I'm now solving",
    excerpt:
      "Almost ten years ago I started Cloud Conformity. Last year I started Circumvent. The most interesting differences aren't the ones I expected.",
    href: '/blog/i-helped-create-the-problem-im-now-solving',
    isExternal: false,
    publishedAt: '2026-07-08',
    category: 'Perspective',
    imageSrc: '/images/blog/second-act-cloud-conformity-to-circumvent.jpg',
    imageAlt:
      'Illustration of the journey from Cloud Conformity detection findings to Circumvent AI-powered reasoning.',
    imageFit: 'cover',
  },
  {
    id: 'ctem-and-the-asset-inventory-problem',
    title: 'CTEM and the Asset Inventory Problem',
    excerpt:
      'Scoping is the first stage of CTEM. When tags drift, discovery needs naming, stack lineage, and access graphs, not API dumps alone.',
    href: '/blog/ctem-and-the-asset-inventory-problem',
    isExternal: false,
    publishedAt: '2026-05-12',
    category: 'Insights',
    imageSrc: '/images/features/icon_Context-graph-base-on-security.svg',
    imageAlt:
      'Resource relationship graph: ingress, identity, workload, service, role, data store, and related nodes with discovery edges.',
    imageFit: 'cover',
    imageModifier: 'context-graph',
    imageSurface: 'dark',
  },
  {
    id: 'soc2-type-1-compliance',
    title: 'Circumvent Achieves SOC 2 Type 1 Compliance, Strengthening Our Commitment to Cloud Security',
    excerpt: 'How our SOC 2 Type 1 milestone reinforces secure-by-design operations for customers.',
    href: '/blog/soc-2-type-1-compliance',
    isExternal: false,
    publishedAt: '2026-05-07',
    category: 'Compliance',
    imageSrc: '/images/blog/soc2-type1-sensiba-badge-white.png',
    imageAlt: 'Sensiba SOC 2 Type 1 badge.',
    imageFit: 'contain',
    imageSurface: 'dark',
  },
  {
    id: 'startup-daily-funding-coverage',
    title: 'Circumvent Raises $6 Million to Launch Multi-Agent AI System to Power Proactive Cloud Security and Remediation Platform',
    excerpt: 'Coverage from Startup Daily on Circumvent’s seed round and roadmap.',
    href: STARTUP_DAILY_ARTICLE_URL,
    isExternal: true,
    publishedAt: '2025-06-16',
    category: 'Press',
    imageSrc: '/images/blog/startup-daily-circumvent-founders.jpg',
    imageAlt: 'Circumvent founders featured in Startup Daily.',
    imageFit: 'cover',
  },
];

const SORTED_BLOG_POSTS = [...BLOG_POSTS].sort(
  (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
);

const BlogLink = ({
  post,
  className = 'blog-card__link',
  children,
  ariaLabel,
  showExternalIcon = false,
}) => {
  const resolvedClassName =
    showExternalIcon && post.isExternal ? `${className} ${className}--external` : className;

  if (post.isExternal) {
    return (
      <a
        href={post.href}
        target="_blank"
        rel="noopener noreferrer"
        className={resolvedClassName}
        aria-label={ariaLabel || `${post.title} (opens in a new tab)`}
      >
        {children || 'Read article'}
        {showExternalIcon && <span className="blog-card__external-icon" aria-hidden="true">↗</span>}
      </a>
    );
  }

  return (
    <Link to={post.href} className={resolvedClassName}>
      {children || 'Read article'}
    </Link>
  );
};

const formatDate = (value) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const BlogPage = () => (
  <>
    <Helmet>
      <title>{BLOG_PAGE_TITLE}</title>
      <meta name="description" content={BLOG_PAGE_DESCRIPTION} />
      <link rel="canonical" href={BLOG_CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={BLOG_PAGE_TITLE}
      description={BLOG_PAGE_DESCRIPTION}
      url={BLOG_CANONICAL_URL}
    />

    <main className="page page--light blog-page">
      <section className="page-hero page-hero--dark" aria-labelledby="blog-hero-heading">
        <div className="page-hero__inner">
          <p className="page-hero__eyebrow">Blog</p>
          <h1 id="blog-hero-heading" className="page-hero__title">
            Product updates, milestones, and security insights
          </h1>
          <p className="page-hero__lead">
            Follow what we are building and how we approach proactive cloud security.
          </p>
        </div>
      </section>

      <section className="page-section page-section--pixel-noise" aria-labelledby="latest-posts-heading">
        <div className="page-container page-container--stack">
          <div className="page-section__header">
            <p className="page-section__eyebrow">Latest posts</p>
            <h2 id="latest-posts-heading">From the Circumvent team</h2>
          </div>

          <div className="blog-posts-grid">
            {SORTED_BLOG_POSTS.map((post) => (
              <article className="blog-card" key={post.id}>
                {post.imageSrc ? (
                  <BlogLink
                    post={post}
                    className={`blog-card__image-link${
                      post.imageSurface ? ` blog-card__image-link--${post.imageSurface}` : ''
                    }`}
                    ariaLabel={`Open article: ${post.title}${post.isExternal ? ' (opens in a new tab)' : ''}`}
                  >
                    <img
                      src={post.imageSrc}
                      alt={post.imageAlt}
                      className={`blog-card__image blog-card__image--${post.imageFit}${
                        post.imageModifier ? ` blog-card__image--${post.imageModifier}` : ''
                      }`}
                      loading="lazy"
                      decoding="async"
                    />
                  </BlogLink>
                ) : null}
                <div className="blog-card__content">
                  <ul className="blog-meta-list blog-meta-list--card" aria-label="Post details">
                    <li>{post.category}</li>
                    <li>
                      <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                    </li>
                  </ul>
                  <h3 className="blog-card__title">{post.title}</h3>
                  <p className="blog-card__excerpt">{post.excerpt}</p>
                  <BlogLink post={post} showExternalIcon />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  </>
);

export default BlogPage;
